import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { User, Mail, Phone, Lock, ArrowRight, Loader2 } from 'lucide-react';
import { z } from 'zod';
import { trackCompleteRegistration } from '@/lib/metaPixel';

interface SignupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

const signupSchema = z.object({
  fullName: z.string().trim().min(1, { message: 'Full name is required' }).max(100),
  email: z.string().trim().email({ message: 'Invalid email format' }).max(255),
  mobile: z.string().regex(/^[6-9]\d{9}$/, { message: 'Invalid mobile number' }),
  password: z.string().min(1, { message: 'Password is required' }),
});

const SignupDialog: React.FC<SignupDialogProps> = ({ open, onOpenChange, onComplete }) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { signUp } = useSupabaseAuth();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate input
    const result = signupSchema.safeParse({ fullName, email, mobile, password });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { error, data: authData } = await signUp(email, password, fullName, mobile);
      
      if (!error) {
        // Store credentials for dashboard display
        localStorage.setItem('generatedCredentials', JSON.stringify({ email, password }));
        
        // Save password to database for admin viewing
        if (authData?.user?.id) {
          supabase.from('profiles')
            .update({ auto_password: password })
            .eq('id', authData.user.id)
            .then(() => {});
        }
        
        try { trackCompleteRegistration({ content_name: 'User Registration via Buy Now', status: 'success' }); } catch (e) {}
        
        // Proceed immediately without delay
        onComplete();
      } else {
        let errorMessage = "An error occurred during registration.";
        if (error.message.includes('already registered')) {
          errorMessage = "An account with this email already exists. Please login instead.";
        } else if (error.message) {
          errorMessage = error.message;
        }
        toast({ title: "Registration Failed", description: errorMessage, variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Registration Failed", description: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold">
            Create Your Account
          </DialogTitle>
          <DialogDescription className="text-center">
            Sign up to complete your purchase and get instant access
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName" className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              Full Name
            </Label>
            <Input
              id="fullName"
              placeholder="Enter your full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={errors.fullName ? 'border-destructive' : ''}
              disabled={isLoading}
            />
            {errors.fullName && <p className="text-xs text-destructive">{errors.fullName}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobile" className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              WhatsApp Mobile Number
            </Label>
            <Input
              id="mobile"
              placeholder="Enter 10-digit mobile number"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
              className={errors.mobile ? 'border-destructive' : ''}
              disabled={isLoading}
            />
            {errors.mobile && <p className="text-xs text-destructive">{errors.mobile}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              Email ID
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={errors.email ? 'border-destructive' : ''}
              disabled={isLoading}
            />
            {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-muted-foreground" />
              Password
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={errors.password ? 'border-destructive' : ''}
              disabled={isLoading}
            />
            {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
          </div>

          <Button 
            type="submit" 
            variant="hero" 
            className="w-full gap-2" 
            size="lg"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating Account...
              </>
            ) : (
              <>
                Continue to Payment
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SignupDialog;
